import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFieldMode } from '../context/FieldModeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  MobileFormHeader, StepIndicator, TouchButton, AutoSaveIndicator
} from './FieldModeComponents';
import {
  ArrowLeft, ArrowRight, Check, Eye, AlertTriangle,
  MapPin, Clock, CheckCircle, Loader2, Camera
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MobileNearMissForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isOnline, syncManager, offlineDB } = useFieldMode();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: What Happened
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    location: '',
    
    // Step 2: Details
    near_miss_type: '',
    description: '',
    potential_severity: '',
    
    // Step 3: Prevention
    immediate_actions: '',
    suggested_controls: ''
  });

  const nearMissTypes = [
    'Slip/Trip', 'Falling Object', 'Equipment Malfunction', 'Chemical Exposure',
    'Electrical', 'Vehicle/Traffic', 'Ergonomic', 'Fire/Explosion', 'Other'
  ];

  const severityLevels = [
    { value: 'low', label: 'Low', description: 'Minor injury possible', color: 'green' },
    { value: 'medium', label: 'Medium', description: 'Medical treatment needed', color: 'yellow' },
    { value: 'high', label: 'High', description: 'Serious injury possible', color: 'orange' },
    { value: 'critical', label: 'Critical', description: 'Life-threatening', color: 'red' }
  ];

  const saveDraft = useCallback(async () => {
    if (!formData.location && !formData.description) return;
    setIsSaving(true);
    try {
      await offlineDB.setUserData('near_miss_draft', { ...formData, updated_at: new Date().toISOString() });
      setLastSaved(new Date().toISOString());
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
    setIsSaving(false);
  }, [formData, offlineDB]);

  useEffect(() => {
    const interval = setInterval(() => {
      void saveDraft();
    }, 30000);
    return () => clearInterval(interval);
  }, [saveDraft]);

  useEffect(() => {
    const initDraft = async () => {
      try {
        const draft = await offlineDB.getUserData('near_miss_draft');
        if (draft) {
          setFormData(draft);
          setLastSaved(draft.updated_at);
          toast({ title: 'Draft Restored', description: 'Your previous draft has been loaded' });
        }
      } catch (error) {
        console.error('Failed to load draft:', error);
      }
    };
    initDraft();
  }, [offlineDB, toast]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    const nearMissData = {
      date: formData.date,
      time: formData.time,
      location: formData.location,
      near_miss_type: formData.near_miss_type,
      description: formData.description,
      potential_severity: formData.potential_severity,
      immediate_actions: formData.immediate_actions,
      suggested_controls: formData.suggested_controls,
      status: 'reported'
    };

    if (isOnline) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/near-misses`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(nearMissData)
        });

        if (response.ok) {
          await offlineDB.setUserData('near_miss_draft', null);
          setShowSuccess(true);
        } else {
          throw new Error('Failed to submit');
        }
      } catch (error) {
        await syncManager.queueForSync('near_miss', nearMissData, '/api/near-misses', 'create');
        await offlineDB.setUserData('near_miss_draft', null);
        setShowSuccess(true);
        toast({
          title: 'Saved Offline',
          description: 'Report will sync when online',
          variant: 'warning'
        });
      }
    } else {
      await syncManager.queueForSync('near_miss', nearMissData, '/api/near-misses', 'create');
      await offlineDB.setUserData('near_miss_draft', null);
      setShowSuccess(true);
    }
    
    setIsSubmitting(false);
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const steps = [
    { number: 1, title: 'What' },
    { number: 2, title: 'Details' },
    { number: 3, title: 'Prevention' }
  ];

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4" data-testid="near-miss-success">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">Report Submitted!</h2>
            <p className="text-green-600 mb-6">
              {isOnline ? 'Your near miss has been recorded.' : 'Saved offline. Will sync when online.'}
            </p>
            <div className="space-y-3">
              <TouchButton onClick={() => navigate('/field')} variant="success">
                Back to Field Mode
              </TouchButton>
              <TouchButton onClick={() => {
                setShowSuccess(false);
                setCurrentStep(1);
                setFormData({
                  date: new Date().toISOString().split('T')[0],
                  time: new Date().toTimeString().slice(0, 5),
                  location: '', near_miss_type: '', description: '',
                  potential_severity: '', immediate_actions: '', suggested_controls: ''
                });
              }} variant="outline">
                Report Another
              </TouchButton>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mobile-near-miss-form">
      <MobileFormHeader
        title="Near Miss"
        subtitle="Report a hazard"
        onBack={() => navigate('/field')}
        icon={Eye}
      />

      <div className="p-4 space-y-4">
        <StepIndicator steps={steps} currentStep={currentStep} />
        <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} />

        {/* Step 1: What Happened */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                  When &amp; Where
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-base">Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleChange('date', e.target.value)}
                      className="h-12 text-base"
                    />
                  </div>
                  <div>
                    <Label className="text-base">Time</Label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => handleChange('time', e.target.value)}
                      className="h-12 text-base"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-base">Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="Where did this occur?"
                    className="h-12 text-base"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Details */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  Near Miss Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-base mb-3 block">Type of Near Miss *</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {nearMissTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() => handleChange('near_miss_type', type)}
                        className={`p-3 rounded-lg text-center text-sm font-medium transition-all ${
                          formData.near_miss_type === type
                            ? 'bg-yellow-100 border-2 border-yellow-500 text-yellow-800'
                            : 'bg-gray-100 border-2 border-transparent text-gray-700'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-base">What happened? *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Describe what almost happened..."
                    className="min-h-[100px] text-base"
                  />
                </div>
                <div>
                  <Label className="text-base mb-3 block">Potential Severity *</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {severityLevels.map((level) => (
                      <button
                        key={level.value}
                        onClick={() => handleChange('potential_severity', level.value)}
                        className={`p-3 rounded-lg text-left transition-all ${
                          formData.potential_severity === level.value
                            ? `bg-${level.color}-100 border-2 border-${level.color}-500`
                            : 'bg-gray-100 border-2 border-transparent'
                        }`}
                      >
                        <span className="font-medium">{level.label}</span>
                        <span className="block text-xs text-gray-500">{level.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Prevention */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Prevention
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-base">Immediate actions taken</Label>
                  <Textarea
                    value={formData.immediate_actions}
                    onChange={(e) => handleChange('immediate_actions', e.target.value)}
                    placeholder="What did you do right away?"
                    className="min-h-[80px] text-base"
                  />
                </div>
                <div>
                  <Label className="text-base">Suggested controls to prevent recurrence</Label>
                  <Textarea
                    value={formData.suggested_controls}
                    onChange={(e) => handleChange('suggested_controls', e.target.value)}
                    placeholder="How can we prevent this?"
                    className="min-h-[80px] text-base"
                  />
                </div>
              </CardContent>
            </Card>

            {!isOnline && (
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="py-4">
                  <p className="font-medium text-yellow-800">You&apos;re currently offline</p>
                  <p className="text-sm text-yellow-700">
                    Your report will be saved locally and synced when you&apos;re back online.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3 pt-4 pb-8">
          {currentStep > 1 && (
            <TouchButton onClick={prevStep} variant="outline" className="flex-1">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </TouchButton>
          )}
          
          {currentStep < 3 ? (
            <TouchButton onClick={nextStep} className="flex-1">
              Continue
              <ArrowRight className="w-5 h-5 ml-2" />
            </TouchButton>
          ) : (
            <TouchButton
              onClick={handleSubmit}
              variant="success"
              className="flex-1"
              disabled={isSubmitting || !formData.location || !formData.description}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Submit Report
                </>
              )}
            </TouchButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileNearMissForm;
